<script lang="ts" setup>
const { awesome } = useAppConfig()
const { parseMenuRoute, parseMenuTitle } = useNavbarParser()
const $screen = useAwesomeScreen()
const nuxtApp = useNuxtApp()
const route = useRoute()

const menus = computed(
  () =>
    (awesome?.layout?.page?.navbar?.menus ||
      []) as AwesomeLayoutPageNavbarMenu[],
)

// drawer
const showDrawer = ref(false)

// 检查菜单项是否激活（支持子路由匹配）
const isMenuItemActive = (menuTo: any) => {
  const to = parseMenuRoute(menuTo)
  if (!to) return false

  // 如果是字符串路径
  if (typeof to === 'string') {
    // 外部链接不检查激活状态
    if (/^https?:\/\//.test(to)) return false
    // 检查路径是否匹配或是否是当前路径的父级
    return route.path === to || route.path.startsWith(to + '/')
  }

  // 如果是路由对象（带 name 属性）
  if ((to as any).name) {
    const targetName = (to as any).name.toString()
    const currentName = route.name?.toString() || ''
    // 精确匹配或子路由匹配（如 news 匹配 news-id）
    return (
      currentName === targetName || currentName.startsWith(targetName + '-')
    )
  }

  return false
}

// 暴露 showDrawer 状态给父组件
defineExpose({
  showDrawer,
})
</script>

<template>
  <header
    class="flex fixed top-0 z-40 w-full flex-none transition-colors duration-300 lg:z-50 bg-transparent"
  >
    <!-- 暗黑模式已禁用: dark:border-gray-50/[0.2] dark:bg-gray-950/[0.5] -->
    <!-- content -->
    <div
      v-if="$screen.higherThan('md', $screen.current.value) || !showDrawer"
      class="flex-1 flex items-center justify-between max-w-screen-2xl mx-auto px-[16px] md:px-[80px] py-[10px]"
    >
      <!-- title -->
      <div>
        <slot name="title">
          <NuxtLink
            to="/"
            :class="
              $screen.higherThan('md', $screen.current.value)
                ? 'font-bold text-lg flex items-center bg-[#F1F1F1B2] py-[16px] px-[12px] rounded-xl'
                : 'flex items-center px-[7px] py-[9px] bg-[#E8E8E8B2] rounded-[6px]'
            "
            style="
              -webkit-backdrop-filter: blur(5px);
              backdrop-filter: blur(5px);
            "
          >
            <img
              src="@/assets/images/logo.png"
              alt="Logo"
              :class="
                $screen.higherThan('md', $screen.current.value)
                  ? 'h-7 w-auto mr-2 inline-block'
                  : 'h-[17px] w-[115px]'
              "
            />
          </NuxtLink>
        </slot>
      </div>
      <!-- menus -->
      <div
        v-if="$screen.higherThan('md', $screen.current.value)"
        class="flex space-x-4 items-center pt-[16px] pb-[24px] px-[16px] bg-[#F1F1F1B2] rounded-xl"
        style="-webkit-backdrop-filter: blur(5px); backdrop-filter: blur(5px)"
        :class="{ 'divide-x divide-gray-500': menus.length > 0 }"
      >
        <div class="flex space-x-4 text-sm items-center">
          <!-- dynamic menus -->
          <template v-for="(item, i) in menus" :key="i">
            <LayoutPageNavbarMenuWrapper :menu="item" />
          </template>
        </div>
        <!-- 语言切换器 - 已注释 -->
        <!-- <div class="pl-4">
          <LanguageSwitcher />
        </div> -->
        <!-- 主题切换器已注释 -->
        <!-- <div class="pl-4 flex space-x-3 text-xl">
          <LayoutPageNavbarDropdownThemeSwitcher />
          <AwesomeLink
            v-if="awesome?.project?.links?.github"
            class="dark:text-gray-400 text-gray-600"
            :href="awesome?.project?.links?.github"
          >
            <Icon name="mdi:github-face" />
          </AwesomeLink>
        </div> -->
      </div>
      <!-- drawer:btn -->
      <div v-else class="flex items-center">
        <button
          class="text-black w-[36px] h-[36px] bg-[#E8E8E8B2] rounded-[6px] flex items-center justify-center"
          style="-webkit-backdrop-filter: blur(5px); backdrop-filter: blur(5px)"
          @click="() => (showDrawer = !showDrawer)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="12"
            viewBox="0 0 16 12"
            fill="none"
          >
            <path
              d="M1 11H15M1 6H15M1 1H15"
              stroke="black"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
    <!-- misc -->
    <!-- drawer - 移动端菜单（最大三分之二屏） -->
    <Transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="!$screen.higherThan('md', $screen.current.value) && showDrawer"
        class="fixed inset-0 z-40"
        @click="showDrawer = false"
      ></div>
    </Transition>

    <!-- drawer - 移动端菜单（最大三分之二屏） -->
    <Transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="!$screen.higherThan('md', $screen.current.value) && showDrawer"
        class="fixed top-0 left-0 right-0 z-50 overflow-y-auto bg-[#F3F3F3B2] max-h-[80vh]"
        style="-webkit-backdrop-filter: blur(5px); backdrop-filter: blur(5px)"
      >
        <!-- 头部：Logo 和关闭按钮 -->
        <div class="flex items-center justify-between px-[16px] py-[10px]">
          <NuxtLink
            to="/"
            class="flex items-center px-[7px] py-[9px] bg-[#E8E8E8B2] rounded-[6px]"
            @click="showDrawer = false"
          >
            <img
              src="@/assets/images/logo.png"
              alt="玻色量子"
              class="h-[17px] w-[115px]"
            />
          </NuxtLink>
          <button
            class="text-black p-[8px] bg-[#E8E8E8B2] rounded-[6px]"
            @click="() => (showDrawer = false)"
          >
            <Icon size="20" name="heroicons:x-mark-20-solid" />
          </button>
        </div>

        <!-- 菜单列表 -->
        <nav class="py-4">
          <template v-for="(item, i) in menus" :key="i">
            <!-- 普通链接 -->
            <template v-if="item?.type === 'link'">
              <NuxtLink
                :to="parseMenuRoute(item.to)"
                class="block p-[16px] text-sm font-medium relative"
                :class="{
                  'bg-[#E8F5F5] border-l-4 border-[#00D4AA] font-medium':
                    isMenuItemActive(item.to),
                  'text-black': !isMenuItemActive(item.to),
                }"
                @click="showDrawer = false"
              >
                {{ parseMenuTitle(item?.title) }}
              </NuxtLink>
            </template>

            <!-- 按钮类型 -->
            <template v-if="item?.type === 'button'">
              <NuxtLink
                :to="parseMenuRoute(item.to)"
                class="block px-6 py-5 text-lg text-gray-800"
                @click="showDrawer = false"
              >
                {{ parseMenuTitle(item?.title) }}
              </NuxtLink>
            </template>

            <!-- 下拉菜单 -->
            <template v-if="item?.type === 'dropdown'">
              <HeadlessDisclosure v-slot="{ open }">
                <HeadlessDisclosureButton
                  class="w-full p-[16px] text-sm font-medium text-left flex items-center justify-between text-black"
                >
                  <span>{{ parseMenuTitle(item?.title) }}</span>
                  <Icon
                    name="heroicons:chevron-down-20-solid"
                    class="text-xl transition-transform duration-300"
                    :class="{ 'transform rotate-180': open }"
                  />
                </HeadlessDisclosureButton>
                <Transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform scale-95 opacity-0"
                  enter-to-class="transform scale-100 opacity-100"
                  leave-active-class="transition duration-150 ease-out"
                  leave-from-class="transform scale-100 opacity-100"
                  leave-to-class="transform scale-95 opacity-0"
                >
                  <HeadlessDisclosurePanel>
                    <template
                      v-for="(child, j) in item?.children || []"
                      :key="j"
                    >
                      <NuxtLink
                        :to="parseMenuRoute(child.to)"
                        class="flex items-center block px-[16px] py-2 text-sm"
                        :class="{
                          'text-[#333] font-medium': isMenuItemActive(child.to),
                          'text-[#333]': !isMenuItemActive(child.to),
                        }"
                        @click="showDrawer = false"
                      >
                        <img
                          class="w-[18px] h-[18px] pr-[5px]"
                          src="@/assets/images/icons/son.png"
                        />
                        {{ parseMenuTitle(child?.title) }}
                      </NuxtLink>
                    </template>
                  </HeadlessDisclosurePanel>
                </Transition>
              </HeadlessDisclosure>
            </template>
          </template>
        </nav>
      </div>
    </Transition>
  </header>
</template>
